import { Fragment, useEffect, useState } from 'react'
import Typo from '@mui/material/Typography'
import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { TypographyProps } from '@mui/material'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(3),
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      maxWidth: '100%',
    },
    classicWrapper: {
      fontFamily: 'monospace',
    },
    classicDigit: {
      fontSize: '1.5rem',
      fontWeight: 700,
      background: theme.palette.text.primary,
      color: theme.palette.primary.main,
      padding: '5px 10px',
      borderRadius: '10px',
      letterSpacing: '1px',
      boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.5)',
      position: 'relative',
      '&.small': {
        fontSize: '1.2rem',
        padding: '3px 5px',
        borderRadius: '5px',
      },
      '&.large': {
        fontSize: '2rem',
        padding: '10px 20px',
        borderRadius: '20px',
      },
    },
    compactWrapper: {
      background: '#fff',
      border: `1px solid ${theme.palette.success.light}`,
      borderRadius: 8,
      boxShadow: '0 4px 14px rgba(0, 0, 0, 0.08)',
      color: theme.palette.success.dark,
      gap: theme.spacing(.6),
      padding: theme.spacing(.55, .9),
      '&.small': {
        gap: theme.spacing(.35),
        padding: theme.spacing(.4, .65),
      },
      '&.large': {
        gap: theme.spacing(.8),
        padding: theme.spacing(.75, 1.2),
      },
    },
    compactItem: {
      display: 'grid',
      justifyItems: 'center',
      minWidth: 44,
      '&.small': {
        minWidth: 34,
      },
      '&.large': {
        minWidth: 56,
      },
    },
    compactValue: {
      fontSize: '1.25rem',
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: 0,
      '&.small': {
        fontSize: '1rem',
      },
      '&.large': {
        fontSize: '1.6rem',
      },
    },
    compactLabel: {
      color: theme.palette.text.secondary,
      fontSize: '.62rem',
      fontWeight: 700,
      lineHeight: 1,
      marginTop: theme.spacing(.35),
      textTransform: 'uppercase',
      '&.small': {
        fontSize: '.55rem',
      },
      '&.large': {
        fontSize: '.68rem',
      },
    },
    compactSeparator: {
      color: theme.palette.success.main,
      fontSize: '1.15rem',
      fontWeight: 700,
      lineHeight: 1,
      '&.small': {
        fontSize: '.95rem',
      },
      '&.large': {
        fontSize: '1.45rem',
      },
    },
    bannerWrapper: {
      background: theme.palette.success.dark,
      borderRadius: 6,
      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.14)',
      color: theme.palette.success.contrastText,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(.55),
      padding: theme.spacing(.65, 1),
      '&.small': {
        gap: theme.spacing(.35),
        padding: theme.spacing(.45, .7),
      },
      '&.large': {
        gap: theme.spacing(.8),
        padding: theme.spacing(.85, 1.35),
      },
    },
    bannerLead: {
      fontSize: '.78rem',
      fontWeight: 700,
      lineHeight: 1,
      opacity: .78,
      textTransform: 'uppercase',
      '&.small': {
        display: 'none',
      },
      '&.large': {
        fontSize: '.86rem',
      },
    },
    bannerPart: {
      alignItems: 'baseline',
      display: 'inline-flex',
      gap: theme.spacing(.25),
      whiteSpace: 'nowrap',
    },
    bannerValue: {
      fontSize: '1.35rem',
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: 0,
      '&.small': {
        fontSize: '1.05rem',
      },
      '&.large': {
        fontSize: '1.8rem',
      },
    },
    bannerUnit: {
      fontSize: '.72rem',
      fontWeight: 700,
      lineHeight: 1,
      opacity: .82,
      '&.small': {
        fontSize: '.62rem',
      },
      '&.large': {
        fontSize: '.82rem',
      },
    },
    bannerSeparator: {
      opacity: .5,
    },
    minimalWrapper: {
      borderBottom: `2px solid ${theme.palette.success.light}`,
      color: theme.palette.success.dark,
      display: 'inline-flex',
      gap: theme.spacing(.35),
      paddingBottom: theme.spacing(.25),
      '&.small': {
        gap: theme.spacing(.2),
      },
      '&.large': {
        gap: theme.spacing(.45),
      },
    },
    minimalPart: {
      alignItems: 'baseline',
      display: 'inline-flex',
      gap: theme.spacing(.2),
      whiteSpace: 'nowrap',
    },
    minimalValue: {
      fontSize: '1.35rem',
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: 0,
      '&.small': {
        fontSize: '1.05rem',
      },
      '&.large': {
        fontSize: '1.8rem',
      },
    },
    minimalUnit: {
      color: theme.palette.text.secondary,
      fontSize: '.72rem',
      fontWeight: 700,
      lineHeight: 1,
      '&.small': {
        fontSize: '.62rem',
      },
      '&.large': {
        fontSize: '.82rem',
      },
    },
    minimalSeparator: {
      color: theme.palette.success.main,
      fontWeight: 800,
      lineHeight: 1,
    },
    purchase: {
      justifyContent: 'flex-start',
      margin: '8px 0 0',
      padding: 0,
      border: 0,
      background: 'transparent',
      boxShadow: 'none',
      color: 'var(--page-builder-text, #17284b)',
      fontVariantNumeric: 'tabular-nums',
      '& $classicDigit': {
        background: '#fff', color: 'inherit', boxShadow: 'none',
        border: '1px solid #ead9c8', borderRadius: 5,
      },
      '& $compactValue, & $bannerValue, & $minimalValue': { color: 'inherit', fontSize: '1.2rem' },
      '& $compactLabel, & $compactSeparator, & $minimalUnit, & $minimalSeparator': { color: 'inherit' },
    },
  })
)

export type CountdownClockDisplayVariant = 'compact' | 'banner' | 'minimal'
type ClockVisualVariant = CountdownClockDisplayVariant | 'classic'
type LegacyVisualVariant = ClockVisualVariant | 'default' | 'alternative'

interface Props extends Omit<TypographyProps, 'component' | 'variant'> {
  dateEnd: Date | string
  className?: string
  component?: TypographyProps['component']
  size?: 'small' | 'medium' | 'large'
  displayVariant?: LegacyVisualVariant
  variant?: LegacyVisualVariant | TypographyProps['variant']
  appearance?: 'default' | 'purchase'
}

const isVisualVariant = (variant: unknown): variant is LegacyVisualVariant => (
  variant === 'compact'
  || variant === 'banner'
  || variant === 'minimal'
  || variant === 'classic'
  || variant === 'default'
  || variant === 'alternative'
)

const normalizeVisualVariant = (
  variant?: LegacyVisualVariant,
): ClockVisualVariant => {
  if (variant === 'compact' || variant === 'banner' || variant === 'minimal') return variant
  if (variant === 'alternative') return 'compact'

  return 'classic'
}

const TextClock: React.FC<Props> = ({
  dateEnd: dateEndProp,
  className,
  size = 'medium',
  displayVariant,
  variant = 'default',
  appearance = 'default',
  style,
  ...rest
}) => {
  const classes = useStyles()
  const [remainingTime, setRemainingTime] = useState([-1, -1, -1])
  const visualVariant = normalizeVisualVariant(
    displayVariant || (isVisualVariant(variant) ? variant : undefined),
  )
  const typographyVariant = variant && !isVisualVariant(variant)
    ? variant
    : undefined

  useEffect(() => {
    const dateEnd = typeof dateEndProp === 'string' ? new Date(dateEndProp) : dateEndProp

    const update = () => {
      const jetzt = new Date()
      const diff = dateEnd.getTime() - jetzt.getTime()

      if (diff > 0) {
        const hours = Math.floor(diff / 1000 / 60 / 60)
        const minutes = Math.floor((diff / 1000 / 60) % 60)
        const seconds = Math.floor((diff / 1000) % 60)
        setRemainingTime([hours, minutes, seconds])
      } else {
        setRemainingTime([0, 0, 0])
      }
    }
    update()
    const interval = setInterval(update, 1000)

    return () => clearInterval(interval)
  }, [dateEndProp])

  // Render the real clock geometry on the server as well. Hidden digits reserve
  // the selected clock style's height until the first client time is available.
  const initialized = remainingTime.every(t => t >= 0)
  if (remainingTime.every(t => t === 0)) return null

  const [hours, minutes, seconds] = remainingTime.map(t => String(Math.max(0, t)).padStart(2, '0'))
  const classNames = (...items: Array<string | undefined | false>) => items.filter(Boolean).join(' ')
  const wrapperClassByVariant: Record<ClockVisualVariant, string> = {
    classic: classes.classicWrapper,
    compact: classes.compactWrapper,
    banner: classes.bannerWrapper,
    minimal: classes.minimalWrapper,
  }
  const rootClassName = classNames(
    classes.root,
    wrapperClassByVariant[visualVariant],
    size,
    className,
    appearance === 'purchase' && classes.purchase,
  )
  const segments = [
    { value: hours, label: 'Std' },
    { value: minutes, label: 'Min' },
    { value: seconds, label: 'Sek' },
  ]

  const renderCompact = () => segments.map((segment, index) => (
    <Fragment key={segment.label}>
      <span className={classNames(classes.compactItem, size)}>
        <span className={classNames(classes.compactValue, size)}>{segment.value}</span>
        <span className={classNames(classes.compactLabel, size)}>{segment.label}</span>
      </span>
      {index < segments.length - 1 && (
        <span className={classNames(classes.compactSeparator, size)} aria-hidden='true'>:</span>
      )}
    </Fragment>
  ))

  const renderBanner = () => <>
    <span className={classNames(classes.bannerLead, size)}>endet in</span>
    {segments.map((segment, index) => (
      <span key={segment.label} className={classes.bannerPart}>
        <span className={classNames(classes.bannerValue, size)}>{segment.value}</span>
        <span className={classNames(classes.bannerUnit, size)}>{segment.label}</span>
        {index < segments.length - 1 && (
          <span className={classes.bannerSeparator} aria-hidden='true'>/</span>
        )}
      </span>
    ))}
  </>

  const renderMinimal = () => segments.map((segment, index) => (
    <span key={segment.label} className={classes.minimalPart}>
      <span className={classNames(classes.minimalValue, size)}>{segment.value}</span>
      <span className={classNames(classes.minimalUnit, size)}>{segment.label}</span>
      {index < segments.length - 1 && (
        <span className={classes.minimalSeparator} aria-hidden='true'>:</span>
      )}
    </span>
  ))

  const renderClassic = () => {
    const digitClass = classNames(classes.classicDigit, size)

    return <>
      <span className={digitClass}>{hours}</span> : 
      <span className={digitClass}>{minutes}</span> : 
      <span className={digitClass}>{seconds}</span>
    </>
  }

  return (
    <Typo {...rest} variant={typographyVariant} className={rootClassName}
      data-discount-clock aria-hidden={!initialized || undefined}
      style={{ ...style, visibility: initialized ? style?.visibility : 'hidden' }}>
      {visualVariant === 'compact' && renderCompact()}
      {visualVariant === 'banner' && renderBanner()}
      {visualVariant === 'minimal' && renderMinimal()}
      {visualVariant === 'classic' && renderClassic()}
    </Typo>
  )
}

export default TextClock
